// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import { applyPenders, type GenericResponseAction } from 'lib/common';
import produce from 'immer';
import * as CommonAPI from 'lib/api/common';

const GET_SECTIONS_WITH_BADGES = 'trending/GET_SECTIONS_WITH_BADGES';
const GET_ALL_BADGES = 'trending/GET_ALL_BADGES';
const GET_BADGE_PRODUCTS = 'trending/GET_BADGE_PRODUCTS';
const SET_SELECTED_BADGE = 'trending/SET_SELECTED_BADGE';
const SET_PRODUCTS = 'trending/SET_PRODUCTS';
const LOAD_MORE_PRODUCTS = 'trending/LOAD_MORE_PRODUCTS';
const CLEAR_PRODUCTS = 'trending/CLEAR_PRODUCTS';

export type BadgeData = {
  id: string,
  name: string,
  color: string,
};

export type SectionWithBadges = {
  id: string,
  title: string,
  description: string,
  thumbnail: string,
  sequence: number,
  limit: number,
  keywords: string,
  type: string,
  is_private: boolean,
  url_slug: string,
  meta: any,
  user: {
    username: string,
  },
  badges: BadgeData[],
};

export type ProductData = {
  id: string,
  title: string,
  thumbnail: string,
  url_slug: string,
  meta: any,
};

export const actionCreators = {
  getSectionsWithBadges: createAction(GET_SECTIONS_WITH_BADGES, CommonAPI.getSectionsWithBadges),
  getAllBadges: createAction(GET_ALL_BADGES, CommonAPI.getAllBadges),
  getBadgeProducts: createAction(
    GET_BADGE_PRODUCTS,
    (badgeId: string, limit?: number, cursor?: string) =>
      CommonAPI.getBadgeProducts(badgeId, limit, cursor),
    (badgeId: string) => badgeId,
  ),
  setSelectedBadge: createAction(SET_SELECTED_BADGE, (badge: ?BadgeData) => badge),
  setProducts: createAction(SET_PRODUCTS, (products: ProductData[]) => products),
  loadMoreProducts: createAction(
    LOAD_MORE_PRODUCTS,
    (badgeId: string, cursor: string) => CommonAPI.getBadgeProducts(badgeId, 12, cursor),
    (badgeId: string) => badgeId,
  ),
  clearProducts: createAction(CLEAR_PRODUCTS),
};

type GetSectionsWithBadgesResponseAction = GenericResponseAction<SectionWithBadges[], void>;
type GetBadgeProductsResponseAction = GenericResponseAction<
  { products: ProductData[], hasMore: boolean, cursor: ?string },
  string,
>;
type SetSelectedBadgeAction = ActionType<typeof actionCreators.setSelectedBadge>;

export type TrendingState = {
  sections: ?(SectionWithBadges[]),
  badges: ?(BadgeData[]),
  selectedBadge: ?BadgeData,
  products: ProductData[],
  hasMore: boolean,
  cursor: ?string,
  loading: boolean,
};

const initialState: TrendingState = {
  sections: null,
  badges: null,
  selectedBadge: null,
  products: [],
  hasMore: false,
  cursor: null,
  loading: false,
};

const reducer = handleActions(
  {
    [SET_SELECTED_BADGE]: (state, { payload }: SetSelectedBadgeAction) => {
      return produce(state, draft => {
        draft.selectedBadge = payload;
      });
    },
    [SET_PRODUCTS]: (state, { payload }: any) => {
      return produce(state, draft => {
        draft.products = payload;
        draft.hasMore = false;
        draft.cursor = null;
      });
    },
    [CLEAR_PRODUCTS]: state => {
      return produce(state, draft => {
        draft.products = [];
        draft.hasMore = false;
        draft.cursor = null;
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_SECTIONS_WITH_BADGES,
    onSuccess: (state: TrendingState, { payload }: GetSectionsWithBadgesResponseAction) => {
      return produce(state, draft => {
        draft.sections = payload.data;
      });
    },
  },
  {
    type: GET_ALL_BADGES,
    onSuccess: (state: TrendingState, { payload }: any) => {
      return produce(state, draft => {
        draft.badges = payload.data;
      });
    },
  },
  {
    type: GET_BADGE_PRODUCTS,
    onPending: (state: TrendingState) => {
      return produce(state, draft => {
        draft.loading = true;
      });
    },
    onSuccess: (state: TrendingState, { payload }: GetBadgeProductsResponseAction) => {
      return produce(state, draft => {
        draft.products = payload.data.products;
        draft.hasMore = payload.data.hasMore;
        draft.cursor = payload.data.cursor;
        draft.loading = false;
      });
    },
    onFailure: (state: TrendingState) => {
      return produce(state, draft => {
        draft.loading = false;
      });
    },
  },
  {
    type: LOAD_MORE_PRODUCTS,
    onPending: (state: TrendingState) => {
      return produce(state, draft => {
        draft.loading = true;
      });
    },
    onSuccess: (state: TrendingState, { payload }: GetBadgeProductsResponseAction) => {
      return produce(state, draft => {
        draft.products = [...draft.products, ...payload.data.products];
        draft.hasMore = payload.data.hasMore;
        draft.cursor = payload.data.cursor;
        draft.loading = false;
      });
    },
    onFailure: (state: TrendingState) => {
      return produce(state, draft => {
        draft.loading = false;
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/trending.js